import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { encrypt, decrypt } from '../utils/encryption';

interface AuthUser {
  Id: number;
  Nombre: string;
  Usuario: string;
  Rol: string;
  Perfil: string;
  AgenteIA: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean; 
  login: (userData: any) => void;
  logout: () => void;
}

// Crear el contexto con un valor inicial explícito
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode; // Asegurarte que children sea del tipo ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); 

  useEffect(() => {
    const encryptedToken = localStorage.getItem('token');
    const encryptedUser = localStorage.getItem('user');
  
    if (encryptedToken && encryptedUser) {
      try {
        const decryptedToken = decrypt(encryptedToken);
        const decryptedUser = JSON.parse(decrypt(encryptedUser));
        setToken(decryptedToken);
        setUser(decryptedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error al recuperar la sesión:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false); 
  }, []);
  
  if (isLoading) {
    return <div>Cargando...</div>; 
  }

  const login = (userData: any) => {
    const { access_token, ...userInfo } = userData;
    const encryptedToken = encrypt(access_token);
    const encryptedUser = encrypt(JSON.stringify(userInfo));

    localStorage.setItem('token', encryptedToken);
    localStorage.setItem('user', encryptedUser);

    setToken(access_token);
    setUser(userInfo);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
